/** A 24-hour `HH:MM` clock time — the format `scheduled_time` is stored in.
 *  Exported so the API's input schemas validate against the same pattern this
 *  module parses with: an unvalidated time silently misses the sundown-slot
 *  check, which compares against exactly these strings. */
export const TIME_PATTERN = /^(0\d|1\d|2[0-3]):[0-5]\d$/

/** Canonical arrival-window length: 4 hours, in minutes. The wizard collects a
 *  window START; the real arrival window runs start → start + 4h. Single source
 *  of truth — web re-exports this from `components/order-flow/types`. */
export const WINDOW_LENGTH_MIN = 4 * 60

/** Earliest a window can start (7:00 AM in minutes-since-midnight) — the start
 *  of the service day, and the first slot the time picker offers. Single source
 *  of truth — web re-exports this (as `WINDOW_START_HOUR`) from
 *  `order-flow/types`, and the scheduling-blackout engine derives its slot grid
 *  from it. */
export const WINDOW_EARLIEST_START_MIN = 7 * 60

/** Latest a window can end (6:00 PM in minutes-since-midnight) — the end of the
 *  service day. Used to clamp a window whose start + 4h would run past close.
 *  Single source of truth — web re-exports this from `order-flow/types`. */
export const WINDOW_LATEST_END_MIN = 18 * 60

/** A selectable delivery slot: its `HH:MM` start (the `value` the picker stores)
 *  and the start/end minutes of its 4-hour arrival window. */
export interface ServiceSlot {
  value: string
  startMin: number
  endMin: number
}

/** The selectable delivery slots, derived once from the window constants:
 *  hourly starts from the 7 AM open through the latest start whose 4-hour window
 *  still ends by the 6 PM close. The single source consumed by both the web
 *  time-grid picker and the scheduling-blackout engine — neither hand-counts the
 *  slot total or the window length, so they can't drift apart. */
export const SERVICE_SLOTS: readonly ServiceSlot[] = (() => {
  const slots: ServiceSlot[] = []
  for (
    let startMin = WINDOW_EARLIEST_START_MIN;
    startMin + WINDOW_LENGTH_MIN <= WINDOW_LATEST_END_MIN;
    startMin += 60
  ) {
    const hour = Math.floor(startMin / 60)
    slots.push({
      value: `${String(hour).padStart(2, '0')}:00`,
      startMin,
      endMin: startMin + WINDOW_LENGTH_MIN,
    })
  }
  return slots
})()

/** Coarse `time_window` bucket → its arrival range, for legacy orders that have
 *  only a named bucket and no fine-grained start time. */
const BUCKET_RANGE: Record<string, { startMin: number; endMin: number }> = {
  morning: { startMin: 7 * 60, endMin: 12 * 60 },
  afternoon: { startMin: 12 * 60, endMin: 16 * 60 },
  evening: { startMin: 16 * 60, endMin: WINDOW_LATEST_END_MIN },
}

/**
 * The timezone the business operates in — the one a `scheduled_date` is a
 * calendar date IN. Server processes run UTC (Fly), so anything deriving
 * "today" for comparison against a scheduled date must say this explicitly
 * rather than trusting the process clock or the DB's `current_date`, both of
 * which roll over 4–8 hours early.
 */
export const BUSINESS_TIMEZONE = 'America/New_York'

/**
 * The calendar date in the viewer's own timezone, as `YYYY-MM-DD` — the format
 * `stop.scheduledDate` / `order_event.scheduled_date` are stored in (a local
 * calendar date, not an instant).
 *
 * Never derive that date with `toISOString().slice(0, 10)`: that is the **UTC**
 * date, which for a US viewer (UTC−4…−8) rolls over to tomorrow at 4–8 PM local
 * — so from early evening onward "today" would silently mean tomorrow.
 *
 * Built from the local date parts rather than `toLocaleDateString('en-CA')`:
 * that formatting is an ICU locale-data convention, and on a small-ICU runtime
 * it silently falls back to `M/D/YYYY`, which the `z.iso.date()` inputs this
 * feeds would then reject.
 */
export function localCalendarDate(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Hoisted because CONSTRUCTING an `Intl.DateTimeFormat` is the entire cost —
 * measured on Bun 1.3.12, 44µs to construct and 0.69µs to format, so building
 * one per call made `businessCalendarDate` 49µs and `earliestSchedulableDate`
 * 132µs. These run on every scheduled-date parse and inside `assertSchedulable`
 * on the unauthenticated `checkout.previewTax`. The options are constant and a
 * formatter is stateless, so there is nothing to reset between calls.
 */
const BUSINESS_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const BUSINESS_CLOCK_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/**
 * Today's calendar date in the BUSINESS timezone, as `YYYY-MM-DD` — the only
 * correct "today" to compare a `scheduled_date` against on the server.
 *
 * `localCalendarDate` reads the *process* clock, which on Fly is UTC: it flips
 * to tomorrow at 20:00 EDT / 19:00 EST, so "today" would mean two different
 * days depending on the hour and the season. That is not a safe floor in
 * either direction — it is simply not the business's date. This asks for the
 * date in `BUSINESS_TIMEZONE` explicitly.
 *
 * Note this is NOT the floor for scheduling: use {@link earliestSchedulableDate},
 * which also accounts for the service day having closed. The ceiling opposite it
 * is {@link latestSchedulableDate}.
 *
 * Read back through `formatToParts` rather than a formatted string: the parts
 * are named, so a runtime whose locale data orders or punctuates the date
 * differently still yields the same result.
 */
export function businessCalendarDate(date: Date = new Date()): string {
  const parts = BUSINESS_DATE_FORMAT.formatToParts(date)
  const part = (type: 'year' | 'month' | 'day'): string | undefined =>
    parts.find((p) => p.type === type)?.value
  const [year, month, day] = [part('year'), part('month'), part('day')]
  const result = `${year}-${month}-${day}`
  // Fail CLOSED. A missing part would yield something like `2026--27`, and '-'
  // sorts below every digit — so every real date would compare as "not past"
  // and the past-date guard would silently disable itself rather than throw.
  if (!year || !month || !day || !/^\d{4}-\d{2}-\d{2}$/.test(result)) {
    throw new Error(
      `Could not derive the business calendar date (got ${result})`,
    )
  }
  return result
}

/**
 * Minutes since midnight on the business timezone's wall clock — the "now" a
 * same-day slot must be compared against.
 *
 * The date floor alone is not enough: at 11 AM ET the calendar date is still
 * today and today is still bookable, but the 7 AM slot has already started. A
 * date-only rule accepts it.
 */
export function businessClockMinutes(date: Date = new Date()): number {
  const parts = BUSINESS_CLOCK_FORMAT.formatToParts(date)
  const part = (type: 'hour' | 'minute'): number =>
    Number(parts.find((p) => p.type === type)?.value)
  const [hour, minute] = [part('hour'), part('minute')]
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    throw new Error('Could not derive the business clock time')
  }
  return hour * 60 + minute
}

/** The last slot start the business still sells on a given day — 14:00, the
 *  latest start whose 4-hour window still ends by the 18:00 close. The day
 *  closes at the last START, not at the last window's END. */
export const LAST_SLOT_START_MIN =
  SERVICE_SLOTS[SERVICE_SLOTS.length - 1]?.startMin ?? WINDOW_EARLIEST_START_MIN

/**
 * Is there still a bookable slot today, on the business wall clock?
 *
 * The single source both the server guard and the picker's date floor read, so
 * the earliest offerable day and the earliest acceptable day cannot disagree —
 * a disagreement here is an offered-then-refused date, which is the failure
 * this whole guard exists to remove.
 *
 * Day granularity is only half of it: within an offerable day the picker must
 * also drop slots that have already started (`slotFloor` in the schedule
 * popover), or the same dead end reappears one level down.
 */
export function hasBookableSlotToday(now: Date = new Date()): boolean {
  return businessClockMinutes(now) < LAST_SLOT_START_MIN
}

/** The earliest date the business will accept a trip for, business-timezone
 *  aware: today while a slot remains, otherwise tomorrow. */
export function earliestSchedulableDate(now: Date = new Date()): string {
  const today = businessCalendarDate(now)
  if (hasBookableSlotToday(now)) return today
  const next = new Date(`${today}T12:00:00Z`)
  next.setUTCDate(next.getUTCDate() + 1)
  return businessCalendarDate(next)
}

/**
 * How far ahead a trip may be scheduled.
 *
 * **An integrity bound, not a commercial horizon.** Ten years is far past
 * anything the business would sell or a customer would book, so it can only
 * ever refuse a typo or a crafted payload — how far ahead a customer may
 * realistically book stays a product decision, made elsewhere and at a
 * different number. A commercial horizon of a year or two was offered and not
 * taken: a bound that doubles as a product rule has to be re-decided the moment
 * sales want a longer window, and one shorter than a test fixture reaches
 * breaks scheduling tests, which is the guard reporting a fixture bug.
 *
 * It exists because the scheduled date is one operand of a product whose other
 * operand was bounded first. `MAX_PREPAID_STORAGE_MONTHS` bounds the months;
 * the billing date is `scheduledDate + months`, so `months: 1` — an ordinary
 * order — on a year-9999 delivery still renders `"+010000-01"`, an expanded
 * year Postgres refuses on `subscription.next_billing_date`, failing that
 * stop's delivery advance for good. The customer's read fails more quietly:
 * `getPalletBilling` returns the same string through a `z.string().nullable()`
 * schema, so the pallet page renders a nonsense date.
 *
 * **The two bounds are only correct together** — a delivery at this horizon
 * plus the full month ceiling must still render a plain date.
 * `prepaid-months-bound.test.ts` asserts that, and it is worth knowing what
 * that assertion can and cannot see: the margin is enormous (the pair does not
 * collide until the horizon passes ~7,870 years), so it is a floor under the
 * relationship, not a measurement of every raise. A realistic change to either
 * number is caught by that constant's own boundary test, not by the pair.
 */
export const SCHEDULE_HORIZON_YEARS = 10

/**
 * The latest date the business will accept a trip for — the ceiling opposite
 * {@link earliestSchedulableDate}'s floor, and business-timezone aware for the
 * same reason.
 *
 * Relative to today, which is why it is a function and not a static `.max()` on
 * a string: a fixed upper literal creeps closer every day until it starts
 * refusing dates that were ordinary when it was written.
 */
export function latestSchedulableDate(now: Date = new Date()): string {
  const today = businessCalendarDate(now)
  const horizon = new Date(`${today}T12:00:00Z`)
  horizon.setUTCFullYear(horizon.getUTCFullYear() + SCHEDULE_HORIZON_YEARS)
  return businessCalendarDate(horizon)
}

/**
 * How far ahead the order flow's date picker lets a customer navigate — the
 * span the blackout calendar is loaded for, so the grid can't reach a day that
 * would render un-greyed and then be refused at checkout.
 *
 * It lives here rather than beside the picker for the reason
 * `LONGEST_OFFERED_STORAGE_MONTHS` does: two layers have to agree about it and
 * neither can see the other. A client offer above what the server accepts is
 * the offered-then-refused dead end the whole scheduling guard exists to
 * remove.
 *
 * **Two ceilings sit above this one, and the widest is the least interesting.**
 * In order: `MAX_RANGE_DAYS` (400, `scheduling-blackout.ts`), which this offer
 * hits FIRST — the engine throws on a wider span, so the picker breaks before
 * any server date rule fires — and then `SCHEDULE_HORIZON_YEARS` (~3652 days),
 * roughly ten times further out. `time.test.ts` asserts against both; an earlier
 * version of this docblock called 400 "the tightest of the three" and pinned
 * only the ~3652 one, naming the binding constraint and then guarding the other.
 *
 * **And a constant-vs-constant assertion is not enough** — `use-blackout.ts` is
 * the only reader, and replacing this call with a literal left every web and
 * shared test green while the grid reached 2040. `use-blackout.test.ts` pins the
 * consumer.
 *
 * None of them are derived from one another. This is a product horizon,
 * `MAX_RANGE_DAYS` is an engine limit, and `SCHEDULE_HORIZON_YEARS` is an
 * integrity bound; they answer to different authorities and move for different
 * reasons, so the invariant is an ordering, never equality.
 */
export const OFFERED_SCHEDULE_HORIZON_DAYS = 366

/** Minutes-since-midnight → "9:00 AM" (always includes minutes so both ends of
 *  a window read consistently). */
function formatMinutes(min: number): string {
  const hour = Math.floor(min / 60)
  const minute = min % 60
  const period = hour < 12 ? 'AM' : 'PM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`
}

/**
 * Format the full 4-hour arrival window for display, e.g. "9:00 AM – 1:00 PM".
 * The selected time is the window START; the window extends 4 hours forward,
 * clamped to the 6 PM service close. When no start time exists, falls back to
 * the coarse `time_window` bucket's range (legacy orders). Returns null when
 * neither is available so callers can render their own placeholder.
 */
export function formatArrivalWindow(
  scheduledTime: string | null | undefined,
  window?: string | null,
): string | null {
  if (scheduledTime && TIME_PATTERN.test(scheduledTime)) {
    const [hour, minute] = scheduledTime.split(':').map(Number)
    const startMin = hour * 60 + minute
    // A start at/after the service close can't yield a forward window; fall
    // through to the bucket (or null) rather than render an inverted range.
    if (startMin < WINDOW_LATEST_END_MIN) {
      const endMin = Math.min(
        startMin + WINDOW_LENGTH_MIN,
        WINDOW_LATEST_END_MIN,
      )
      return `${formatMinutes(startMin)} – ${formatMinutes(endMin)}`
    }
  }
  const bucket = window ? BUCKET_RANGE[window] : undefined
  if (bucket) {
    return `${formatMinutes(bucket.startMin)} – ${formatMinutes(bucket.endMin)}`
  }
  return null
}

/**
 * Format a bare `YYYY-MM-DD` (a Postgres `date` — a schedule date, a
 * prepaid-through or next-charge boundary) with its year. A booked trip or a
 * prepaid window can cross into next year, and a bare "Jan 3" would read as
 * the past.
 *
 * The `T00:00:00` is load-bearing: `new Date('2026-07-16')` is parsed as UTC
 * midnight, which renders as the 15th anywhere west of Greenwich. Appending the
 * time forces local midnight, so the date shown is the date stored — on the
 * server (UTC) and in the browser alike.
 *
 * Lives here rather than in the web app because the API also renders dates into
 * customer-facing copy (the one-pending-trip refusal), and a second copy of
 * this would drift from what the pallet page shows.
 */
export function formatFullDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * The weekday a ground carrier does not run, so it never consumes one of a
 * zone's transit days: Sunday (`0`, the JS weekday numbering).
 *
 * The business calendar and the carrier's service calendar are NOT the same
 * calendar, and this constant is the whole of the difference. Our blackout
 * engine blocks every Saturday and blocks nothing on a Sunday — a Sunday is a
 * day we will happily deliver on. A freight carrier quoting "3 business days"
 * runs Monday to Friday, so counting Sunday as a transit day would offer an
 * arrival a day earlier than the freight can physically reach: Zone 3 from a
 * Thursday would read Monday where the carrier's own Fri/Mon/Tue lands Tuesday.
 * Ruled by Marcus, over the two alternatives — reuse the blackout
 * list unchanged (over-promises by up to a day per week of transit) and give
 * each zone its own carrier calendar (no data source behind it today).
 *
 * The asymmetry runs the other way for the days the blackout engine blocks and
 * the carrier does not — a Jewish observance is an ordinary working day for a
 * freight line — and that is deliberate too: we cannot hand the freight over on
 * a day we are shut, so the day is genuinely lost to the customer. The rule as
 * given is "if a date was blocked out either way, it does not count as one of
 * them", which is conservative in exactly that direction.
 */
const CARRIER_NON_TRANSIT_WEEKDAY = 0

/**
 * How far ahead {@link earliestCarrierArrival} will look before giving up. A
 * bound rather than a `while (true)`: the caller supplies the blocked-day
 * predicate, so a predicate that answers `true` for every day — a bug, or a
 * calendar loaded for the wrong range — would otherwise spin forever inside a
 * request. Five transit days can never need more than a few weeks of calendar,
 * so any value this large means the predicate is wrong, not the date.
 */
const MAX_TRANSIT_SEARCH_DAYS = 400

/** `YYYY-MM-DD` + `days`, as pure calendar arithmetic. */
function addCalendarDays(iso: string, days: number): string {
  const anchor = new Date(`${iso}T00:00:00Z`)
  anchor.setUTCDate(anchor.getUTCDate() + days)
  return anchor.toISOString().slice(0, 10)
}

/** Weekday (0=Sun … 6=Sat) of a `YYYY-MM-DD` calendar date. */
function calendarWeekday(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay()
}

/**
 * When a carrier zone's freight should arrive: `transitDays` workable days
 * after `anchorDateIso`, counting only the days the freight actually moves.
 *
 * A carrier zone is named for how long the freight takes to get there, so the
 * customer is told to expect it around that many days after the carrier
 * collects — an ESTIMATE, never a bound on what they may pick. Two kinds of day
 * are skipped and never consume one of the transit days —
 * {@link CARRIER_NON_TRANSIT_WEEKDAY} (the carrier does not run) and any day
 * `isFullyBlocked` marks (we are shut, so we cannot hand the freight over) —
 * and the count simply carries on past them.
 *
 * **The returned day needs no rolling forward, and that is a property rather
 * than an omission.** It is by construction the LAST counted transit day, so it
 * is neither blocked nor a Sunday: it is always itself a bookable day. Written
 * as one loop for that reason — a separate "now roll the answer past a blocked
 * day" pass would be dead code that reads as load-bearing.
 *
 * Pure calendar arithmetic on a `YYYY-MM-DD` string: no clock, no timezone, and
 * therefore nothing to inject. The anchor is already a calendar date — whoever
 * derived it from an instant did so through `businessCalendarDate`, which is
 * where the timezone question belongs and is answered.
 *
 * Both ends call this one function — the estimate the destination pill shows
 * the customer, and the estimate checkout records for ops — so the date ops see
 * is the date the customer was shown. They differ only in where the
 * blocked-day predicate comes from: the browser reads the blackout calendar it
 * already loaded, the server calls the engine.
 *
 * @param anchorDateIso the day the carrier collects — the move date the
 *   customer picks for a carrier destination, recorded as
 *   `order_event.requested_pickup_date`.
 * @param transitDays the zone's promise, 1–5. A zone with no transit days
 *   (quote-only) makes no promise and must not reach this function at all.
 * @param isFullyBlocked whether the whole day is unavailable to us.
 * @throws when `transitDays` is not a positive integer, when the anchor is not
 *   a calendar date, or when no arrival is found inside
 *   {@link MAX_TRANSIT_SEARCH_DAYS}. Every one of those is a caller bug rather
 *   than a date a customer could pick, so it fails loudly.
 */
export function earliestCarrierArrival(
  anchorDateIso: string,
  transitDays: number,
  isFullyBlocked: (iso: string) => boolean,
): string {
  if (!Number.isInteger(transitDays) || transitDays < 1) {
    throw new Error(
      `earliestCarrierArrival needs a positive whole number of transit days, got ${transitDays}`,
    )
  }
  if (Number.isNaN(new Date(`${anchorDateIso}T00:00:00Z`).getTime())) {
    throw new Error(
      `earliestCarrierArrival got an invalid date: ${anchorDateIso}`,
    )
  }

  let counted = 0
  for (let step = 1; step <= MAX_TRANSIT_SEARCH_DAYS; step++) {
    const day = addCalendarDays(anchorDateIso, step)
    if (calendarWeekday(day) === CARRIER_NON_TRANSIT_WEEKDAY) continue
    if (isFullyBlocked(day)) continue
    counted++
    if (counted === transitDays) return day
  }
  throw new Error(
    `earliestCarrierArrival found no ${transitDays}-day arrival within ${MAX_TRANSIT_SEARCH_DAYS} days of ${anchorDateIso}`,
  )
}

/** A scheduled arrival, in either of the two shapes the schema stores: a precise
 *  `HH:MM` window start, or a coarse legacy `time_window` bucket. Both may be
 *  absent — an Order Event carrying neither is dated but not timed. */
export interface ArrivalSlot {
  scheduledTime?: string | null
  timeWindow?: string | null
}

/** The arrival window an `ArrivalSlot` occupies, in minutes since midnight, or
 *  `null` when it carries no readable time at all.
 *
 *  A precise start wins over a bucket (it is the finer answer, and the bucket is
 *  only kept for legacy rows). A start at or past the service close yields no
 *  forward window and falls through to the bucket, matching
 *  `formatArrivalWindow` — the two must agree, or a slot renders as one range
 *  and is compared as another. */
export function arrivalWindowMinutes(slot: ArrivalSlot): {
  startMin: number
  endMin: number
} | null {
  const { scheduledTime, timeWindow } = slot
  if (scheduledTime && TIME_PATTERN.test(scheduledTime)) {
    const [hour, minute] = scheduledTime.split(':').map(Number)
    const startMin = hour * 60 + minute
    if (startMin < WINDOW_LATEST_END_MIN) {
      return {
        startMin,
        endMin: Math.min(startMin + WINDOW_LENGTH_MIN, WINDOW_LATEST_END_MIN),
      }
    }
  }
  return timeWindow ? (BUCKET_RANGE[timeWindow] ?? null) : null
}

/**
 * May `pickup` run on the SAME calendar date as `delivery`?
 *
 * True only when the pickup's window STARTS strictly after the delivery's window
 * ENDS: a customer cannot hand back a pallet they are still waiting to receive,
 * and a window that merely overlaps is two trucks at one address in one slot.
 * The caller owns the date comparison — this answers only the same-date case.
 *
 * The two degenerate cases resolve in OPPOSITE directions, and each follows an
 * established rule rather than a preference:
 *
 * - **The delivery carries no readable time → permitted.** There is no promise
 *   to break, and a rule with nothing behind it must not invent one.
 * - **The delivery is timed but the pickup is not → refused.** Fail CLOSED, the
 *   same way `assertSchedulable` refuses an unparseable arrival slot: an
 *   unreadable time must not buy a free pass past the slot rule. An untimed
 *   pickup could land at the 7 AM open, hours before the delivery it follows.
 */
export function slotFollowsOnSameDate(
  pickup: ArrivalSlot,
  delivery: ArrivalSlot,
): boolean {
  const deliveryWindow = arrivalWindowMinutes(delivery)
  if (!deliveryWindow) return true
  const pickupWindow = arrivalWindowMinutes(pickup)
  if (!pickupWindow) return false
  return pickupWindow.startMin > deliveryWindow.endMin
}

/**
 * The first selectable slot that could follow `delivery` on its own date, or
 * `null` when none can — which is the caller's signal that the earliest
 * possible pickup is the NEXT day.
 *
 * Derived from `SERVICE_SLOTS` rather than computed from the window constants,
 * so it can only ever name a slot the picker actually offers. That is what lets
 * the picker and the server floor on one answer at slot granularity, which
 * `.agents/reference/scheduling.md` requires: a value either side derived
 * independently is an offered-then-refused slot waiting to happen.
 *
 * Returns the earliest slot when the delivery carries no readable time, matching
 * `slotFollowsOnSameDate`'s permissive branch for that case.
 */
export function earliestFollowingSlot(delivery: ArrivalSlot): string | null {
  return (
    SERVICE_SLOTS.find((candidate) =>
      slotFollowsOnSameDate({ scheduledTime: candidate.value }, delivery),
    )?.value ?? null
  )
}
